/**
 * 详情快捷联动 → 商品库：按印花组构建 colorSlots（1 主图 = 1 SKC，细节图挂主图下）。
 */

import {
  getDedupedCompositeResultsForGroup,
  buildGoodsCompositeSelectionId,
  parseGoodsCompositeSelectionId,
} from '@/utils/goodsCompositeSelection';
import { stripImageFileExtension } from '@/utils/imageUrlUtils';
import { isGenericListingImageName } from '@/pages/ipe/workbench/utils/platformListingItemMedia';
import {
  groupSlidesByMain,
  enrichSlideHierarchyFields,
} from '@/image-designer/utils/templateImageHierarchy';
import { resolveExportPreviewImageColumn } from './buildGoodsExportPreviewData';
import { extractColorCodeFromPatternName, extractColorFromImageNames } from './listingPatternColorMatch';

function trimText(value) {
  return String(value || '').trim();
}

/** 优先可读原名，跳过「主图1 / 细节1」等占位名 */
function pickReadableSlotName(...candidates) {
  for (const raw of candidates) {
    const name = stripImageFileExtension(trimText(raw));
    if (name && !isGenericListingImageName(name)) return name;
  }
  return '';
}

function firstStampNameFromForm(form, groupIndex = 0) {
  const group = Array.isArray(form?.imageGroups) ? form.imageGroups[groupIndex] : null;
  if (!group || typeof group !== 'object') return '';
  for (const value of Object.values(group)) {
    if (value && typeof value === 'object') {
      const name = pickReadableSlotName(value.name, value.fileName, value.title);
      if (name) return name;
    }
  }
  return '';
}

export function pickGoodsCompositeUrl(result) {
  return trimText(
    result?.compositeUrl || result?.url || result?.dataUrl || result?.composite_url,
  );
}

function templateImageById(form, imageId) {
  const images = form?.templateData?.images || [];
  return images.find((img) => String(img?.id) === String(imageId)) || null;
}

function resolveSlotColor(mainTpl, mainName, detailNames = []) {
  const colorLabel = trimText(mainTpl?.colorLabel);
  const fromNames = extractColorFromImageNames([mainName, ...detailNames]);
  const codeFromPattern = extractColorCodeFromPatternName(mainName);
  return {
    colorLabel: colorLabel || fromNames.colorLabel,
    colorCode: fromNames.colorCode || codeFromPattern || trimText(mainTpl?.colorCode),
  };
}

/**
 * 将主图创作成品与商品模板 SKC 对齐。
 * 默认不回退到 slots[0]，避免所有颜色都落到第一个黑色 SKC。
 */
export function matchSourceColorSlot(sourceSlots = [], {
  mainName = '',
  resultIndex = null,
  index = null,
  colorCode = '',
  colorLabel = '',
  allowIndexFallback = false,
} = {}) {
  const slots = Array.isArray(sourceSlots) ? sourceSlots : [];
  if (slots.length === 0) return null;

  const name = trimText(mainName);
  if (name) {
    const byName = slots.find((slot) => trimText(slot?.mainName) === name);
    if (byName) return byName;
  }

  const code = trimText(colorCode).toUpperCase();
  if (code) {
    const byCode = slots.find((slot) => trimText(slot?.colorCode).toUpperCase() === code);
    if (byCode) return byCode;
  }

  const label = trimText(colorLabel);
  if (label) {
    const byLabel = slots.find((slot) => trimText(slot?.colorLabel) === label);
    if (byLabel) return byLabel;
  }

  if (resultIndex != null && Number.isFinite(Number(resultIndex))) {
    const byResult = slots.find((slot) => {
      const info = parseGoodsCompositeSelectionId(slot?.selectionId);
      return info && Number(info.resultIndex) === Number(resultIndex);
    });
    if (byResult) return byResult;
    if (slots[Number(resultIndex)]) return slots[Number(resultIndex)];
  }

  if (allowIndexFallback && index != null && Number.isFinite(Number(index))) {
    return slots[Number(index)] || null;
  }
  return null;
}

/**
 * 主图创作主图 + 商品模板颜色/细节 → 完整 colorSlot。
 * 中文颜色优先取模板创建时选择的颜色。
 */
export function buildMergedMainColorSlot({
  mainUrl,
  mainName = '',
  selectionId = '',
  templateImage = null,
  sourceSlot = null,
} = {}) {
  const url = trimText(mainUrl);
  if (!url) return null;
  const resolvedName = stripImageFileExtension(mainName)
    || trimText(mainName)
    || trimText(sourceSlot?.mainName)
    || '主图1';
  const fromTpl = {
    colorLabel: trimText(templateImage?.colorLabel),
    colorCode: trimText(templateImage?.colorCode),
  };
  const fromSource = {
    colorLabel: trimText(sourceSlot?.colorLabel),
    colorCode: trimText(sourceSlot?.colorCode),
  };
  const fromName = extractColorFromImageNames([resolvedName]);
  return {
    patternName: resolvedName,
    colorLabel: fromSource.colorLabel || fromTpl.colorLabel || fromName.colorLabel || '',
    colorCode: fromSource.colorCode
      || fromTpl.colorCode
      || fromName.colorCode
      || extractColorCodeFromPatternName(resolvedName)
      || '',
    mainUrl: url,
    mainName: resolvedName,
    detailUrls: [...(Array.isArray(sourceSlot?.detailUrls) ? sourceSlot.detailUrls : [])],
    detailNames: [...(Array.isArray(sourceSlot?.detailNames) ? sourceSlot.detailNames : [])],
    selectionId: trimText(sourceSlot?.selectionId) || trimText(selectionId),
  };
}

/**
 * 单个印花组 → colorSlots（模板主图层级优先，否则按 kind 列归属）。
 *
 * @param {object} form goodsForm
 * @param {number} groupIndex 印花组索引
 * @param {{
 *   formIndex?: number,
 *   billingNav?: string,
 *   selectedResultIndexes?: number[]|null,
 *   excludedSelectionIds?: string[]|Set<string>,
 * }} [options]
 * selectedResultIndexes=null 表示组内全部主图 SKC（仍会排除 excludedSelectionIds）
 */
export function buildColorSlotsForGoodsPrintGroup(form, groupIndex, options = {}) {
  const billingNav = options.billingNav === 'podProductDesign' ? 'podProductDesign' : 'goods';
  const formIndex = Number(options.formIndex ?? 0);
  const selectedSet = Array.isArray(options.selectedResultIndexes)
    ? new Set(options.selectedResultIndexes.map(Number))
    : null;
  const excludedSet = new Set(
    [...(options.excludedSelectionIds || [])].map((id) => String(id || '').trim()).filter(Boolean),
  );

  const isExcludedSelection = (resultIndex) => {
    if (excludedSet.size === 0) return false;
    return excludedSet.has(buildGoodsCompositeSelectionId(formIndex, groupIndex, resultIndex));
  };

  const deduped = getDedupedCompositeResultsForGroup(form, groupIndex);
  const templateImages = (form?.templateData?.images || []).map((img) => enrichSlideHierarchyFields(img));
  const hierarchyGroups = groupSlidesByMain(templateImages);

  const resultByImageId = new Map();
  deduped.forEach((result, resultIndex) => {
    const imageId = trimText(result?.imageId);
    if (!imageId) return;
    resultByImageId.set(imageId, { result, resultIndex });
  });

  const slots = [];

  hierarchyGroups.forEach(({ main, details }) => {
    const mainHit = resultByImageId.get(String(main.id));
    if (!mainHit) return;
    if (selectedSet && !selectedSet.has(mainHit.resultIndex)) return;
    if (isExcludedSelection(mainHit.resultIndex)) return;

    const mainUrl = pickGoodsCompositeUrl(mainHit.result);
    if (!mainUrl) return;

    const stampName = firstStampNameFromForm(form, groupIndex);
    const mainName = pickReadableSlotName(
      mainHit.result?.name,
      main.name,
      templateImageById(form, main.id)?.name,
      stampName,
    ) || `主图${slots.length + 1}`;
    const detailUrls = [];
    const detailNames = [];

    details.forEach((detailTpl) => {
      const detailHit = resultByImageId.get(String(detailTpl.id));
      if (!detailHit) return;
      if (isExcludedSelection(detailHit.resultIndex)) return;
      const url = pickGoodsCompositeUrl(detailHit.result);
      if (!url) return;
      detailUrls.push(url);
      detailNames.push(
        pickReadableSlotName(detailHit.result?.name, detailTpl.name, stampName)
        || `细节${detailUrls.length}`,
      );
    });

    const color = resolveSlotColor(main, mainName, detailNames);
    slots.push({
      patternName: mainName,
      colorCode: color.colorCode,
      colorLabel: color.colorLabel,
      mainUrl,
      mainName,
      detailUrls,
      detailNames,
      selectionId: buildGoodsCompositeSelectionId(formIndex, groupIndex, mainHit.resultIndex),
    });
  });

  if (slots.length > 0) return slots;

  const mainResults = [];
  const detailResults = [];
  deduped.forEach((result, resultIndex) => {
    if (selectedSet && !selectedSet.has(resultIndex)) return;
    if (isExcludedSelection(resultIndex)) return;
    const url = pickGoodsCompositeUrl(result);
    if (!url) return;
    const column = resolveExportPreviewImageColumn(result, form, billingNav);
    if (column === 'main') mainResults.push({ result, resultIndex, url });
    else detailResults.push({ result, resultIndex, url });
  });

  const stampNameFallback = firstStampNameFromForm(form, groupIndex);
  mainResults.forEach(({ result, resultIndex, url }, index) => {
    const tpl = templateImageById(form, result.imageId);
    const mainName = pickReadableSlotName(result?.name, tpl?.name, stampNameFallback)
      || `主图${index + 1}`;
    const color = resolveSlotColor(tpl, mainName);
    const detailUrls = index === 0 ? detailResults.map((row) => row.url) : [];
    const detailNames = index === 0
      ? detailResults.map((row, i) => pickReadableSlotName(
        row.result?.name,
        templateImageById(form, row.result.imageId)?.name,
        stampNameFallback,
      ) || `细节${i + 1}`)
      : [];
    slots.push({
      patternName: mainName,
      colorCode: color.colorCode || trimText(tpl?.colorCode),
      colorLabel: color.colorLabel || trimText(tpl?.colorLabel),
      mainUrl: url,
      mainName,
      detailUrls,
      detailNames,
      selectionId: buildGoodsCompositeSelectionId(formIndex, groupIndex, resultIndex),
    });
  });

  if (slots.length === 0 && detailResults.length > 0) {
    const first = detailResults[0];
    const tpl = templateImageById(form, first.result.imageId);
    const mainName = pickReadableSlotName(first.result?.name, tpl?.name, stampNameFallback)
      || '主图1';
    const detailUrls = detailResults.map((row) => row.url);
    const detailNames = detailResults.map((row, i) => (
      pickReadableSlotName(
        row.result?.name,
        templateImageById(form, row.result.imageId)?.name,
        stampNameFallback,
      ) || `细节${i + 1}`
    ));
    const color = resolveSlotColor(tpl, mainName, detailNames);
    slots.push({
      patternName: mainName,
      colorCode: color.colorCode,
      colorLabel: color.colorLabel,
      mainUrl: first.url,
      mainName,
      detailUrls,
      detailNames,
      selectionId: buildGoodsCompositeSelectionId(formIndex, groupIndex, first.resultIndex),
    });
  }

  return slots;
}

/**
 * 单张结果图 → 独立 SPU 的 colorSlot（无主图创作手动上传等）。
 */
export function buildSingleMainColorSlot(url, name = '', options = {}) {
  const mainUrl = trimText(url);
  if (!mainUrl) return null;
  const mainName = stripImageFileExtension(name) || trimText(name) || '主图1';
  const color = extractColorFromImageNames([mainName]);
  return {
    patternName: mainName,
    colorCode: color.colorCode,
    colorLabel: color.colorLabel,
    mainUrl,
    mainName,
    detailUrls: [],
    detailNames: [],
    selectionId: trimText(options.selectionId),
  };
}

/**
 * 将 colorSlots 汇总为刊登桥接 source 顶层字段。
 */
export function summarizeColorSlotsForBridgeSource(colorSlots = []) {
  const slots = Array.isArray(colorSlots) ? colorSlots : [];
  const mainUrls = slots.map((slot) => trimText(slot?.mainUrl)).filter(Boolean);
  const detailUrls = slots.flatMap((slot) => (
    Array.isArray(slot?.detailUrls) ? slot.detailUrls : []
  )).map(trimText).filter(Boolean);
  const mainImageNames = slots.map((slot) => trimText(slot?.mainName)).filter(Boolean);
  return {
    url: mainUrls[0] || '',
    imageUrls: mainUrls,
    detailImageUrls: [...new Set(detailUrls)],
    mainImageNames,
    colorSlots: slots,
  };
}

function listingUrlKey(url) {
  const raw = trimText(url);
  if (!raw) return '';
  const uploadsAt = raw.indexOf('/uploads/');
  if (uploadsAt >= 0) return raw.slice(uploadsAt).split('?')[0];
  return raw.split('?')[0];
}

function sameListingUrl(a, b) {
  const left = listingUrlKey(a);
  const right = listingUrlKey(b);
  return Boolean(left) && left === right;
}

function uniqueListingUrls(list = []) {
  const out = [];
  (Array.isArray(list) ? list : []).forEach((raw) => {
    const url = trimText(raw);
    if (!url || out.some((existing) => sameListingUrl(existing, url))) return;
    out.push(url);
  });
  return out;
}

/**
 * 主图创作产出 ↔ POD 模板主图 的覆盖对。
 * 优先用步骤上显式 podMainOverlays；否则用 POD 主图列表与创作 URL 按序 zip。
 */
export function collectGoodsStudioOverlays(goodsStep, podStep, goodsUrls = []) {
  const explicit = (Array.isArray(goodsStep?.podMainOverlays) ? goodsStep.podMainOverlays : [])
    .map((row) => ({
      sourceUrl: trimText(row?.sourceUrl),
      outputUrl: trimText(row?.outputUrl),
    }))
    .filter((row) => row.sourceUrl && row.outputUrl);
  if (explicit.length > 0) {
    return { overlays: explicit, fromPodMains: true };
  }

  const podMains = uniqueListingUrls(
    Array.isArray(podStep?.mainCompositeImageUrls) ? podStep.mainCompositeImageUrls : [],
  );
  const outputs = uniqueListingUrls(goodsUrls);
  if (podMains.length > 0 && outputs.length > 0) {
    const n = Math.min(podMains.length, outputs.length);
    const overlays = [];
    for (let i = 0; i < n; i += 1) {
      overlays.push({ sourceUrl: podMains[i], outputUrl: outputs[i] });
    }
    return { overlays, fromPodMains: true };
  }

  return {
    overlays: outputs.map((outputUrl) => ({ sourceUrl: '', outputUrl })),
    fromPodMains: false,
  };
}

/**
 * 主图创作产出按「源 POD 主图 URL」对齐到颜色槽，再覆盖 mainUrl。
 * - 有 POD 主图映射时：绝不把多余创作图塞进首色细节（那些是其它颜色/细节误带的主图）
 * - 无映射时：按序覆盖；仅非 POD 路径才把多出来的图进首色细节
 */
export function overlayGoodsStudioOnColorSlots(slots, goodsStep, options = {}) {
  const prepared = (Array.isArray(slots) ? slots : [])
    .map((slot) => {
      const mainUrl = trimText(slot?.mainUrl);
      if (!mainUrl) return null;
      const detailUrls = [];
      const detailNames = [];
      const rawDetails = Array.isArray(slot?.detailUrls) ? slot.detailUrls : [];
      const rawNames = Array.isArray(slot?.detailNames) ? slot.detailNames : [];
      rawDetails.forEach((raw, index) => {
        const url = trimText(raw);
        if (!url || sameListingUrl(url, mainUrl)) return;
        if (detailUrls.some((existing) => sameListingUrl(existing, url))) return;
        detailUrls.push(url);
        detailNames.push(trimText(rawNames[index]) || `细节${detailUrls.length}`);
      });
      return {
        patternName: trimText(slot?.patternName || slot?.mainName),
        mainName: trimText(slot?.mainName || slot?.patternName),
        mainUrl,
        colorCode: trimText(slot?.colorCode),
        colorLabel: trimText(slot?.colorLabel),
        detailUrls,
        detailNames,
      };
    })
    .filter(Boolean);
  if (prepared.length === 0) return [];

  const templateMainUrls = prepared.map((slot) => slot.mainUrl);
  const goodsUrls = uniqueListingUrls(
    Array.isArray(options.goodsUrls) && options.goodsUrls.length
      ? options.goodsUrls
      : [
        ...(Array.isArray(goodsStep?.compositeImageUrls) ? goodsStep.compositeImageUrls : []),
        goodsStep?.outputImageUrl,
        goodsStep?.imageUrl,
      ],
  );
  const { overlays, fromPodMains } = collectGoodsStudioOverlays(
    goodsStep,
    options.podStep,
    goodsUrls,
  );

  const stripTemplateMainsFromDetails = (list, blocked) => list.map((slot) => {
    const detailUrls = [];
    const detailNames = [];
    (slot.detailUrls || []).forEach((url, index) => {
      if (!url || blocked.some((main) => sameListingUrl(main, url))) return;
      detailUrls.push(url);
      detailNames.push(slot.detailNames?.[index] || `细节${detailUrls.length}`);
    });
    return { ...slot, detailUrls, detailNames };
  });

  if (overlays.length === 0) {
    return stripTemplateMainsFromDetails(prepared, templateMainUrls);
  }

  const next = prepared.map((slot) => ({
    ...slot,
    detailUrls: [...slot.detailUrls],
    detailNames: [...slot.detailNames],
  }));
  const usedOutputKeys = new Set();
  const assignedSlotIndexes = new Set();

  // 1) 源 POD 主图 URL → 颜色槽主图（与模板层级一致，不被细节图插队）
  overlays.forEach((pair) => {
    if (!pair.sourceUrl) return;
    const slotIndex = next.findIndex((slot, idx) => (
      !assignedSlotIndexes.has(idx) && sameListingUrl(slot.mainUrl, pair.sourceUrl)
    ));
    if (slotIndex < 0) return;
    const outputUrl = trimText(pair.outputUrl);
    if (!outputUrl) return;
    next[slotIndex] = { ...next[slotIndex], mainUrl: outputUrl };
    assignedSlotIndexes.add(slotIndex);
    usedOutputKeys.add(listingUrlKey(outputUrl));
  });

  // 2) 未命中的槽位再按剩余产出顺序补齐
  const remaining = overlays.filter((pair) => {
    const key = listingUrlKey(pair.outputUrl);
    return Boolean(key) && !usedOutputKeys.has(key);
  });
  let remainIdx = 0;
  for (let i = 0; i < next.length && remainIdx < remaining.length; i += 1) {
    if (assignedSlotIndexes.has(i)) continue;
    const outputUrl = trimText(remaining[remainIdx]?.outputUrl);
    remainIdx += 1;
    if (!outputUrl) continue;
    next[i] = { ...next[i], mainUrl: outputUrl };
    assignedSlotIndexes.add(i);
    usedOutputKeys.add(listingUrlKey(outputUrl));
  }

  // 3) POD 主图路径：多余创作图丢弃；非 POD 才把多出来的进首色细节
  if (!fromPodMains) {
    remaining.slice(remainIdx).forEach((pair) => {
      const detailUrl = trimText(pair?.outputUrl);
      if (!detailUrl) return;
      if (next.some((slot) => sameListingUrl(slot.mainUrl, detailUrl))) return;
      if (next[0].detailUrls.some((existing) => sameListingUrl(existing, detailUrl))) return;
      next[0].detailUrls.push(detailUrl);
      next[0].detailNames.push(`细节${next[0].detailUrls.length}`);
    });
  }

  const blockedMains = [
    ...templateMainUrls,
    ...next.map((slot) => slot.mainUrl),
  ].filter(Boolean);
  return stripTemplateMainsFromDetails(next, blockedMains);
}
